import { Insight } from '../../../components/office';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, Star, Search, Sparkles, Loader2, ArrowRight,
  Sun, Clock, Activity, PlayCircle, CalendarDays, Gauge, Percent, Users,
  FileText, Banknote, Receipt, Target, Layers, Layers3, Package, Handshake,
  GitCompare, Trophy, Grid3X3, Wrench, Bell, CalendarClock, Lock, Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { reportsIntelApi } from '@verlo/shared/services/api';
import { Input } from '@verlo/shared/components/ui/input';
import { Button } from '@verlo/shared/components/ui/button';
import { SearchField } from '@verlo/shared/components/ui/search-field';

const ICONS = {
  Sun, Clock, Activity, PlayCircle, CalendarDays, Gauge, Percent, Users,
  FileText, Banknote, Receipt, Target, Layers, Layers3, Package, Handshake,
  GitCompare, Trophy, Grid3X3, Wrench, Bell, CalendarClock, Tag,
};

const CADENCE_ORDER = ['daily', 'weekly', 'monthly', 'adhoc'];
const CADENCE_LABEL = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', adhoc: 'Ad-hoc' };
const CATEGORY_LABEL = {
  operations: 'Operations', finance: 'Finance', intelligence: 'Intelligence',
  build: 'Build', alerts: 'Alerts',
};

const ReportsLibraryPage = () => {
  const [reports, setReports] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const [lib, favs] = await Promise.all([
          reportsIntelApi.library(),
          reportsIntelApi.libraryFavorites(),
        ]);
        setReports(lib.data.reports || []);
        setFavorites(favs.data.favorites || []);
      } catch {
        toast.error('Could not load Reports Library');
      }
    })();
  }, []);


  const toggleFav = async (key) => {
    const isFav = favorites.includes(key);
    setFavorites((f) => (isFav ? f.filter((k) => k !== key) : [...f, key]));
    try {
      if (isFav) await reportsIntelApi.removeFavorite(key);
      else await reportsIntelApi.addFavorite(key);
    } catch { /* rollback silently — next reload will correct */ }
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return reports.filter((r) => {
      if (!r.accessible) return false;
      if (tab !== 'all' && r.cadence !== tab && !(tab === 'favorites' && favorites.includes(r.key))) return false;
      if (tab === 'favorites' && !favorites.includes(r.key)) return false;
      if (!query) return true;
      return `${r.name} ${r.desc} ${CATEGORY_LABEL[r.category] || ''}`.toLowerCase().includes(query);
    });
  }, [reports, q, tab, favorites]);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of filtered) {
      const key = tab === 'all' ? r.cadence : r.category;
      if (!g[key]) g[key] = [];
      g[key].push(r);
    }
    return g;
  }, [filtered, tab]);

  const groupOrder = tab === 'all' ? CADENCE_ORDER : Object.keys(grouped);
  const favReports = reports.filter((r) => favorites.includes(r.key) && r.accessible);

  return (
    <ReportsLayout>
      <div data-testid="reports-library-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="text-[13px] text-ink-soft mt-1">
              Standard hospitality reports for daily, weekly and monthly workflows, plus intelligence, alerts and your own dashboards. Star the ones you live in.
            </p>
          </div>
        </div>

        {/* Daily priority — Verlo Insight's three things to action today */}
        <Insight
          read={() => reportsIntelApi.dailyPriority(null)}
          headline="What needs my attention today?"
          purpose="A scan of today's trade: the three things that need action, what each is costing, and the report to open."
          generateLabel="Scan today"
          className="mb-6"
          testId="daily-priority"
          render={(priority) => (
            <div className="grid gap-3 md:grid-cols-3">
              {(priority.items || []).map((it, i) => (
                <div key={i} className="rounded-md border border-hairline bg-surface-2 p-3" data-testid={`daily-priority-item-${i}`}>
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">#{i + 1}</span>
                    {it.impact_gbp != null && (
                      <span className="font-mono text-[12.5px] font-semibold text-signal-warn">£{Math.abs(Math.round(it.impact_gbp)).toLocaleString('en-GB')}/day</span>
                    )}
                  </div>
                  <p className="mb-1 text-[13.5px] font-semibold text-ink">{it.title}</p>
                  <p className="text-[12.5px] leading-relaxed text-ink-soft">{it.detail}</p>
                  {it.action_href && (
                    <Link to={it.action_href} className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-semibold text-verlo hover:underline" data-testid={`daily-priority-action-${i}`}>
                      {it.action || 'Open report'} <ArrowRight size={11} />
                    </Link>
                  )}
                </div>
              ))}
              {priority.items?.length === 0 && (
                <p className="text-[13px] text-ink-soft md:col-span-3">No urgent priorities right now. Trade is on track.</p>
              )}
            </div>
          )}
        />

        {/* Favourites row */}
        {favReports.length > 0 && (
          <div className="mb-5" data-testid="library-favorites">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-soft mb-2 flex items-center gap-1.5">
              <Star size={11} className="fill-signal-warn text-signal-warn" /> My reports
            </div>
            <div className="flex flex-wrap gap-2">
              {favReports.map((r) => {
                const Icon = ICONS[r.icon] || FileText;
                return (
                  <Link key={r.key} to={r.path} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-hairline hover:border-ink-faint rounded-md text-[12px] font-semibold text-ink transition-colors" data-testid={`library-fav-${r.key}`}>
                    <Icon size={13} className="text-verlo" />
                    {r.name}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <SearchField className="flex-1 min-w-[260px] max-w-md" placeholder="Search reports…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="library-search" />
          <div className="flex gap-1 bg-paper p-0.5 rounded-md">
            {[
              { key: 'all',       label: 'All' },
              { key: 'favorites', label: 'Starred', testid: 'library-tab-starred' },
              { key: 'daily',     label: 'Daily' },
              { key: 'weekly',    label: 'Weekly' },
              { key: 'monthly',   label: 'Monthly' },
              { key: 'adhoc',     label: 'Ad-hoc' },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-1 text-[12.5px] font-semibold rounded ${tab === t.key ? 'bg-white text-ink ' : 'text-ink-soft hover:text-ink'}`}
                data-testid={t.testid || `library-tab-${t.key}`}
              >{t.label}</button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {groupOrder.map((groupKey) => {
          const rows = grouped[groupKey];
          if (!rows?.length) return null;
          return (
            <div key={groupKey} className="mb-6">
              <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-soft mb-2">
                {CADENCE_LABEL[groupKey] || CATEGORY_LABEL[groupKey] || groupKey}
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {rows.map((r) => {
                  const Icon = ICONS[r.icon] || FileText;
                  const isFav = favorites.includes(r.key);
                  return (
                    <div key={r.key} className="bg-white border border-hairline rounded-lg p-4 hover:border-ink-faint  transition-all relative group" data-testid={`library-card-${r.key}`}>
                      <button
                        onClick={() => toggleFav(r.key)}
                        className={`absolute top-3 right-3 ${isFav ? 'text-signal-warn' : 'text-ink-faint hover:text-signal-warn'}`}
                        title={isFav ? 'Remove from starred' : 'Star this report'}
                        data-testid={`library-star-${r.key}`}
                      >
                        <Star size={14} className={isFav ? 'fill-signal-warn' : ''} />
                      </button>
                      <Link to={r.path} className="block">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-8 h-8 rounded-md bg-paper flex items-center justify-center">
                            <Icon size={14} className="text-verlo" />
                          </span>
                          <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft">{CATEGORY_LABEL[r.category] || r.category}</span>
                        </div>
                        <h3 className="text-[14px] font-semibold text-ink mb-1 pr-6">{r.name}</h3>
                        <p className="text-[12px] text-ink-soft leading-relaxed line-clamp-2">{r.desc}</p>
                        <div className="mt-3 pt-3 border-t border-hairline flex items-center justify-between text-[12.5px] text-ink-soft">
                          <span className="capitalize">{CADENCE_LABEL[r.cadence] || r.cadence}</span>
                          <span className="inline-flex items-center gap-0.5 font-semibold text-verlo group-hover:gap-1.5 transition-all">
                            Open <ArrowRight size={11} />
                          </span>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {reports.some((r) => !r.accessible) && (
          <div className="mt-4 text-[12.5px] text-ink-faint inline-flex items-center gap-1">
            <Lock size={10} /> {reports.filter((r) => !r.accessible).length} report{reports.filter((r) => !r.accessible).length === 1 ? '' : 's'} hidden by your role permissions.
          </div>
        )}
      </div>
    </ReportsLayout>
  );
};

export default ReportsLibraryPage;
